import React, { useCallback, useEffect, useState } from "react";

import api, {
  benefitValidationApi,
  clearToken,
  getToken,
  setToken,
} from "./api";

import { Logo } from "./ui";


const STATUS_LABELS = {
  active: "Activo",
  used: "Utilizado",
  cancelled: "Cancelado",
};


export default function BenefitValidation({ identifier }) {
  const [sessionChecked, setSessionChecked] = useState(false);
  const [allyUser, setAllyUser] = useState(null);
  const [benefit, setBenefit] = useState(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");


  const loadBenefit = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await benefitValidationApi.validate(identifier);
      setBenefit(data);
    } catch (e) {
      setBenefit(null);

      if (e?.status === 404) {
        setError(
          "Este beneficio no pertenece a este establecimiento o ya no está disponible."
        );
      } else if (e?.status === 409) {
        setError(e?.message || "Este beneficio ya no puede validarse.");
      } else if (e?.status === 401) {
        clearToken();
        setAllyUser(null);
      } else {
        setError(e?.message || "No pudimos validar este beneficio.");
      }
    } finally {
      setLoading(false);
    }
  }, [identifier]);


  useEffect(() => {
    let mounted = true;

    async function restoreSession() {
      if (!getToken()) {
        if (mounted) setSessionChecked(true);
        return;
      }

      try {
        const user = await api.auth.me();

        if (!mounted) return;

        if (user?.role === "ally" && user?.ally_id) {
          setAllyUser(user);
        } else {
          clearToken();
          setAllyUser(null);
          setError(
            "La sesión actual no corresponde a una cuenta de aliado PawCita."
          );
        }
      } catch {
        clearToken();
      } finally {
        if (mounted) setSessionChecked(true);
      }
    }

    restoreSession();

    return () => {
      mounted = false;
    };
  }, []);


  useEffect(() => {
    if (sessionChecked && allyUser) {
      loadBenefit();
    }
  }, [sessionChecked, allyUser, loadBenefit]);


  async function handleLogin(e) {
    e.preventDefault();

    if (!email.trim() || !password) {
      setError("Ingresa tu correo y contraseña.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const res = await api.auth.login({
        email: email.trim(),
        password,
      });

      if (res?.user?.role !== "ally" || !res?.user?.ally_id) {
        clearToken();
        setAllyUser(null);
        setError(
          "Esta cuenta no está autorizada como aliado PawCita."
        );
        return;
      }

      setToken(res.token);
      setAllyUser(res.user);
    } catch (e2) {
      setError(e2?.message || "No pudimos iniciar sesión.");
    } finally {
      setLoading(false);
    }
  }


  async function handleComplete() {
    if (!benefit?.can_complete || completing) return;

    setCompleting(true);
    setError("");
    setSuccess("");

    try {
      const updated = await benefitValidationApi.complete(identifier);

      setBenefit({
        ...updated,
        can_complete: false,
      });

      setSuccess("Beneficio confirmado correctamente.");
    } catch (e) {
      setError(e?.message || "No pudimos confirmar el beneficio.");
    } finally {
      setCompleting(false);
    }
  }


  function switchAccount() {
    clearToken();
    setAllyUser(null);
    setBenefit(null);
    setSuccess("");
    setError("");
    setPassword("");
  }


  function goToAllyPanel() {
    window.location.href = "/aliados";
  }


  if (!sessionChecked) {
    return (
      <Page>
        <Card>
          <p className="text-center text-sm text-slate-500">
            Verificando acceso…
          </p>
        </Card>
      </Page>
    );
  }


  if (!allyUser) {
    return (
      <Page>
        <Card>
          <Brand />

          <h1 className="mt-5 text-center font-display text-2xl font-black text-[#0B2D4D]">
            Validar beneficio PawCita
          </h1>

          <p className="mt-2 text-center text-sm leading-6 text-slate-500">
            Inicia sesión con la cuenta autorizada del establecimiento
            para validar el beneficio.
          </p>

          <form onSubmit={handleLogin} className="mt-7 space-y-4">
            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700">
                Correo
              </label>

              <input
                data-testid="benefit-ally-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-slate-400"
                placeholder="correo@establecimiento.com"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700">
                Contraseña
              </label>

              <input
                data-testid="benefit-ally-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-slate-400"
                placeholder="••••••••"
              />
            </div>

            {error ? <ErrorMessage>{error}</ErrorMessage> : null}

            <button
              data-testid="benefit-ally-login"
              type="submit"
              disabled={loading}
              className="w-full rounded-2xl bg-[#0B2D4D] px-5 py-3.5 font-black text-white disabled:opacity-50"
            >
              {loading ? "Ingresando…" : "Ingresar como aliado"}
            </button>
          </form>
        </Card>
      </Page>
    );
  }


  return (
    <Page>
      <Card>
        <Brand />

        <p className="mt-5 text-center text-xs font-black uppercase tracking-widest text-[#FF5C5C]">
          Beneficio PawCita
        </p>

        {loading && !benefit ? (
          <p className="mt-6 text-center text-sm text-slate-500">
            Validando beneficio…
          </p>
        ) : null}

        {error ? (
          <div className="mt-6">
            <ErrorMessage>{error}</ErrorMessage>

            <button
              type="button"
              onClick={goToAllyPanel}
              data-testid="benefit-back-to-ally-panel"
              className="mt-4 w-full rounded-2xl bg-[#0B2D4D] px-5 py-3 font-black text-white"
            >
              Volver al Panel de Aliados
            </button>

            <button
              type="button"
              onClick={switchAccount}
              className="mt-3 w-full px-4 py-2 text-sm font-bold text-slate-500"
            >
              Usar otra cuenta
            </button>
          </div>
        ) : null}

        {benefit ? (
          <>
            <h1
              data-testid="benefit-validation-code"
              className="mt-3 text-center text-2xl font-black text-[#0B2D4D]"
            >
              {benefit.code}
            </h1>

            <div className="mt-7 rounded-3xl border border-slate-200 bg-white p-5">
              <Row label="Aliado" value={benefit.ally_name} />
              <Row label="Mascota" value={benefit.dog_name} />

              <div className="my-4 border-t border-slate-100" />

              <div>
                <p className="text-xs font-black uppercase tracking-wider text-slate-400">
                  Beneficio
                </p>

                <p
                  className="mt-2 text-base font-black leading-6 text-[#0B2D4D]"
                  data-testid="benefit-validation-description"
                >
                  {benefit.benefit || "Beneficio PawCita"}
                </p>
              </div>

              <div className="my-4 border-t border-slate-100" />

              <Row
                label="Estado"
                value={STATUS_LABELS[benefit.status] || benefit.status}
                strong
              />
            </div>

            {success ? (
              <div
                className="mt-5 rounded-2xl bg-emerald-50 p-4 text-center text-sm font-black text-emerald-700"
                data-testid="benefit-validation-success"
              >
                {success}
              </div>
            ) : null}

            {benefit.can_complete ? (
              <button
                type="button"
                onClick={handleComplete}
                disabled={completing}
                data-testid="benefit-complete"
                className="mt-6 w-full rounded-2xl bg-[#67D5B5] px-5 py-4 font-black text-[#0B2D4D] disabled:opacity-50"
              >
                {completing
                  ? "Confirmando…"
                  : "Confirmar beneficio utilizado"}
              </button>
            ) : null}

            <p className="mt-5 text-center text-xs leading-5 text-slate-400">
              PawCita registra únicamente la utilización del beneficio.
              El pago y la atención se gestionan directamente con el establecimiento.
            </p>

            <button
              type="button"
              onClick={goToAllyPanel}
              className="mt-5 w-full px-4 py-2 text-sm font-black text-[#0B2D4D]"
            >
              Volver al Panel de Aliados
            </button>
          </>
        ) : null}
      </Card>
    </Page>
  );
}


function Page({ children }) {
  return (
    <div className="min-h-screen bg-[#FFF8F1] px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-lg">{children}</div>
    </div>
  );
}


function Card({ children }) {
  return (
    <div className="rounded-[30px] border border-slate-100 bg-white p-6 shadow-xl shadow-slate-200/40 sm:p-8">
      {children}
    </div>
  );
}


function Brand() {
  return (
    <div className="text-center">
      <div className="flex justify-center">
        <Logo />
      </div>

      <p className="mt-2 text-xs font-bold text-slate-400">
        Paws + Humans, Better Stories
      </p>
    </div>
  );
}


function Row({ label, value, strong = false }) {
  return (
    <div className="flex items-start justify-between gap-5 py-2">
      <span className="text-sm text-slate-500">{label}</span>

      <span
        className={`text-right text-sm ${
          strong ? "font-black text-[#0B2D4D]" : "font-bold text-slate-800"
        }`}
      >
        {value || "—"}
      </span>
    </div>
  );
}


function ErrorMessage({ children }) {
  return (
    <div className="rounded-2xl bg-rose-50 px-4 py-3 text-sm font-bold leading-5 text-rose-700">
      {children}
    </div>
  );
}
